import { getAchievementCategoryTemplateId, isPremiumStartParam } from '@focuslee/shared-mini-app';
import * as MuiIcons from '@mui/icons-material';
import { addQuestCompletion } from '../client/apollo';
import { ListQuestCompletion } from '../client/types';

type HealthLeeQuestCompletionStartParam = {
    id: string;
    name: string;
    description: string;
    categoryId: string;
    dueDate?: string | null;
    completedXp: number;
};

export const fixOverscroll = () => {
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
            document.body.style.height = window.visualViewport?.height + 'px';
        });
    }
    // This will ensure user never overscroll the page
    window.addEventListener('scroll', () => {
        if (window.scrollY > 0) window.scrollTo(0, 0);
    });
};

export const navigateIfOAuthParamsPresent = (tgWebAppStartParam?: string) => {
    if (tgWebAppStartParam?.startsWith('oauth_')) {
        const rest = tgWebAppStartParam.slice('oauth_'.length);
        const lastUnderscore = rest.lastIndexOf('_');
        const sessionId = lastUnderscore !== -1 ? rest.slice(0, lastUnderscore) : rest;
        const clientId = lastUnderscore !== -1 ? rest.slice(lastUnderscore + 1) : 'client';
        window.location.hash = `/oauth/${sessionId}/${clientId}`;
    }
};

export const navigateIfAchievementCategoryParamsPresent = (tgWebAppStartParam?: string) => {
    const templateCategoryId = getAchievementCategoryTemplateId(tgWebAppStartParam);
    if (templateCategoryId) window.location.hash = `/achievements/category/${templateCategoryId}`;
};

export const navigateIfPremiumParamPresent = (tgWebAppStartParam?: string) => {
    if (isPremiumStartParam(tgWebAppStartParam)) window.location.hash = '/premium';
};

export const addHealthLeeQuestCompletionFromStartParam = (tgWebAppStartParam?: string) => {
    const prefix = 'healthlee-quest-completed-';
    if (!tgWebAppStartParam?.startsWith(prefix)) return;

    try {
        const completion = JSON.parse(fromBase64Url(tgWebAppStartParam.slice(prefix.length))) as HealthLeeQuestCompletionStartParam;
        if (!completion.id || !completion.name || !completion.categoryId) return;
        addQuestCompletion({
            quest: {
                id: completion.id,
                name: completion.name,
                description: completion.description,
                categoryId: completion.categoryId,
                dueDate: completion.dueDate ?? null,
            },
            completedXp: completion.completedXp,
            completedAchievements: [],
            progressedAchievements: [],
            goal: null,
            eventLog: null,
        } as unknown as ListQuestCompletion);
    } catch {
        return;
    }
};

const fromBase64Url = (value: string): string => {
    const base64 = value
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(value.length / 4) * 4, '=');
    return decodeURIComponent(atob(base64));
};

// Utility promise-based delay
export const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export const getRandomElement = <T>(list: T[]): T => {
    const randomIndex = Math.floor(Math.random() * list.length);
    return list[randomIndex];
};

const iconBlacklist = new Set([
    'battery_20',
    'battery_30',
    'battery_50',
    'battery_60',
    'battery_80',
    'battery_90',
    'battery_charging_20',
    'battery_charging_30',
    'battery_charging_50',
    'battery_charging_60',
    'battery_charging_80',
    'battery_charging_90',
    'access_alarms',
    'drive_file_rename',
    'eight_k',
    'eight_k_plus',
    'eight_mp',
    'eighteen_mp',
    'eighteen_up_rating',
    'eightteen_mp',
    'eleven_mp',
    'fifteen_mp',
    'five_g',
    'five_k',
    'five_k_plus',
    'five_mp',
    'fivteen_mp',
    'four_g_mobiledata',
    'four_g_plus_mobiledata',
    'four_k',
    'four_k_plus',
    'four_mp',
    'fourteen_mp',
    'google',
    'git_hub',
    'instagram',
    'nine_k',
    'nine_k_plus',
    'nine_mp',
    'nineteen_mp',
    'one_k',
    'one_k_plus',
    'one_kk',
    'seven_k',
    'seven_k_plus',
    'seven_mp',
    'seventeen_mp',
    'signal_cellular_1_bar',
    'signal_cellular_2_bar',
    'signal_cellular_3_bar',
    'signal_cellular_connected_no_internet_1_bar',
    'signal_cellular_connected_no_internet_2_bar',
    'signal_cellular_connected_no_internet_3_bar',
    'signal_wifi_1_bar',
    'signal_wifi_1_bar_lock',
    'signal_wifi_2_bar',
    'signal_wifi_2_bar_lock',
    'signal_wifi_3_bar',
    'signal_wifi_3_bar_lock',
    'six_k',
    'six_k_plus',
    'six_mp',
    'sixteen_mp',
    'sixty_fps',
    'sixty_fps_select',
    'thirteen_mp',
    'thirty_fps',
    'thirty_fps_select',
    'three_d_rotation',
    'three_g_mobiledata',
    'three_k',
    'three_k_plus',
    'three_p',
    'three_sixty',
    'times_one_mobiledata',
    'twelve_mp',
    'twenty_four_mp',
    'twenty_one_mp',
    'twenty_three_mp',
    'twenty_two_mp',
    'twenty_zero_mp',
    'twitter',
    'two_k',
    'two_k_plus',
    'two_mp',
    'whats_app',
    'x',
    'you_tube',
]);
const iconPostProcessMapping: Record<string, string> = {
    co_2: 'co2',
    grid_3x_3: 'grid_3x3',
    grid_4x_4: 'grid_4x4',
};
const iconVariants = ['Outlined', 'Outline', 'TwoTone', 'Sharp', 'Rounded'];

export const allIconNames = Array.from(
    new Set(
        Object.keys(MuiIcons)
            .filter(name => !iconVariants.some(variant => name.endsWith(variant)))
            .map(name => {
                let processedName = name;
                if (processedName.endsWith('Filled')) {
                    processedName = processedName.slice(0, -6);
                }
                return processedName
                    .replace(/([a-z])([A-Z])/g, '$1_$2')
                    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
                    .replace(/([a-z])([0-9])/g, '$1_$2')
                    .replace(/([0-9])([A-Z])/g, '$1_$2')
                    .toLowerCase();
            }),
    ),
)
    .filter(name => !iconBlacklist.has(name))
    .map(name => iconPostProcessMapping[name] || name);

export const getRandomIcon = () => getRandomElement(allIconNames);
